import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { AXNUIDefaultModal } from "@iddataweb/idw-common-ui/containers";
import { setSubmissionSubmitting } from '@redux/actions/submission-actions';
import {
	getAsyncTimeoutID,
	getDCUICancelCount,
	getDCUICancelMax,
	getShowAsyncModalCancelButton
} from '@redux/selectors/verification-form-selectors';
import {
	setAsyncModalMessage,
	setAsyncTimeoutId,
	setDCUICancelCount,
	setIsDcuiProcessCompleted,
	setShowAsyncModal,
	setShowAsyncModalCancelButton,
	updateAttributeAsyncStatus
} from '@redux/actions/verification-form-actions';
import { Logger } from '@utils/logger';
import Modal from 'react-bootstrap/Modal';
import Button from "react-bootstrap/Button";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMobileAlt } from '@fortawesome/free-solid-svg-icons/faMobileAlt';
import styles from "./async-modal.less";
import { pollForAsyncSuccess } from "@requests/poll-for-async-success";
import { datadogRum } from '@datadog/browser-rum';
import { setError } from '@redux/actions/error-actions';
import { AttributeInput } from '@idw-types/data';
import {
	getAsi,
	getAsyncInterval,
	getOidcCallbackURL,
	getRedirectToCallbackUrlOnError,
	getTmxInDCUI
} from "@redux/selectors/verification-configuration-selectors";
import { getContextPath } from "@redux/selectors/query-parameters-selectors";
import { setShouldUpdateSubmitButton } from "@redux/actions/common-actions";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import get from "lodash/get";
import { handleNavigationForError } from "@utils/navigation-util";

type AsyncModalProps = {
	message: string;
	hasMaxRetries: boolean;
	asyncMethod: string;
};

export const AsyncModal = (props: AsyncModalProps) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const asi = useSelector(getAsi);
	const contextPath = useSelector(getContextPath);
	const asyncTimeoutID = useSelector(getAsyncTimeoutID);
	const asyncInterval = useSelector(getAsyncInterval);
	const dcuiCancelCount = useSelector(getDCUICancelCount);
	const dcuiCancelMax = useSelector(getDCUICancelMax);
	const showAsyncModalCancelButton = useSelector(getShowAsyncModalCancelButton);
	const tmxInDCUI = useSelector(getTmxInDCUI);
	const redirectToCallbackUrlOnError = useSelector(getRedirectToCallbackUrlOnError);
	const oidcCallbackURL = useSelector(getOidcCallbackURL);
	const asyncPollErrorCount = useRef(0);
	const [disableCancelButton, setDisableCancelButton] = useState(false);

	const {
		message,
		hasMaxRetries,
		asyncMethod
	} = props;

	const content = useSelector(getLanguageContent);

	const handlePollForAsyncSuccess = () => {
		if (tmxInDCUI) {
			//submit form
			setTimeout(() => {
				const submitButton = document.getElementById("verify-all");
				if (submitButton) {
					submitButton.click();
				}
			}, 300);
		}

		//close modal
		dispatch(setShowAsyncModal(false));
		dispatch(setShouldUpdateSubmitButton(true));
	};

	const setupTimeoutToPollForAsyncSuccess = () => {
		if (asyncTimeoutID) {
			Logger.info(`Cleared async timeout ${asyncTimeoutID}`);
			clearTimeout(asyncTimeoutID);
		}
		const newAsyncTimeoutID = setTimeout(() => { performPollForAsyncSuccess() }, asyncInterval);
		Logger.info(`Created new timeout ${newAsyncTimeoutID}`);
		dispatch(setAsyncTimeoutId(newAsyncTimeoutID));
	};

	const handleErrorWithFaultTolerance = (error: Error) => {
		Logger.error(error);
		datadogRum.addError(error);
		asyncPollErrorCount.current++;
		setupTimeoutToPollForAsyncSuccess();
	};

	const performPollForAsyncSuccess = async () => {
		try {
			const response: Response = await pollForAsyncSuccess(asi, contextPath);

			const error = {
				name: "Poll for Async Status Error",
				asi,
				errorCode: `${response.status}`,
				message: response.statusText,
				errorURI: response.url,
				httpStatus: `${response.status}`,
			};

			if (!response.ok) {
				throw error;
			} else {
				const data = await response.json();

				if (data.status === 'fail' && (hasMaxRetries || data.errorCode)) {
					throw {
						...error,
						name: "Poll for Async Status Responded with Fail",
						forwardedErrorCode: data.errorCode,
						forwardedErrorDescription: data.errorMessage
					};
				} else if (data.status === 'fail') {
					Logger.error(error);
					datadogRum.addError(error);
					handleAsyncCancel();
				} else if (data.status === 'pending') {
					// Not ready yet
					Logger.info('Async request incomplete. Try again');
					setupTimeoutToPollForAsyncSuccess();
				} else if (data.status === 'success') {
					Logger.info('Async request complete. Handling response');
					//verify data
					Logger.info('Received async response');

					// Set that dcui is completed even if there is no attribute
					dispatch(setIsDcuiProcessCompleted(true));

					//set new status and values for attributes
					const asyncAttributeList = data.docAttrList;
					asyncAttributeList.forEach((asyncAttribute: AttributeInput) => {
						dispatch(updateAttributeAsyncStatus(asyncAttribute));
					});

					dispatch(setAsyncModalMessage("common.asyncModal.complete"));
					setDisableCancelButton(true);

					Logger.info(`Change isFormSubmitting to: false`);
					dispatch(setSubmissionSubmitting(false));
					setTimeout(() => {
						handlePollForAsyncSuccess();
					}, 600);
				}
			}
		} catch (error) {
			if (asyncPollErrorCount.current > 0) {
				Logger.error(error);
				datadogRum.addError(error);
				dispatch(setError(error));
				handleNavigationForError(dispatch)(
					redirectToCallbackUrlOnError,
					oidcCallbackURL,
					asi,
					"/error",
					navigate,
					location.pathname,
					location.search,
					error
				);
			} else {
				handleErrorWithFaultTolerance(error);
			}
		}
	};

	useEffect(() => {
		performPollForAsyncSuccess();
	}, []);

	const handleAsyncCancel = () => {
		// verify data
		Logger.info('User has cancelled async');
		clearTimeout(asyncTimeoutID);
		dispatch(setAsyncModalMessage('common.asyncModal.tryAgain'));
		setDisableCancelButton(true);

		setTimeout(() => {
			// avanderwoude 10-22-2018 Only allow user to cancel once, without counter
			// info no way to tell when is last chance. Can remove this when we have counter info on the front end
			if (dcuiCancelCount + 1 >= dcuiCancelMax) {
				dispatch(setShowAsyncModalCancelButton(false));
			}

			dispatch(setDCUICancelCount(dcuiCancelCount + 1));
			dispatch(setShowAsyncModal(false));
		}, 600);

		dispatch(setSubmissionSubmitting(false));
		dispatch(setShouldUpdateSubmitButton(true));
	};

	return (
		<AXNUIDefaultModal
			ariaLabelledBy={"async-modal-title"}
			className={styles.asyncModal}
			dialogClassName={styles.asyncModalDialog}
			isMaxWidth={false}
			onHide={undefined}
			show={true}
		>
			<Modal.Body className={styles.asyncModalBody}>
				<FontAwesomeIcon icon={faMobileAlt} className={styles.mobileIcon} />
				<span className={styles.textSpan} dangerouslySetInnerHTML={{
					__html: get(content.common.asyncModal, `continueInfo${asyncMethod}`)
				}} />
				<span id="async-details" className={styles.textSpan}>{content.common.asyncModal.details}</span>
				<span id="async-message" className={styles.textSpan}>{get(content, message) || message}</span>
			</Modal.Body>
			<Modal.Footer className={styles.asyncModalFooter}>
				{!hasMaxRetries && showAsyncModalCancelButton && (
					<Button
						id={"async-cancel-button"}
						disabled={disableCancelButton}
						variant={"secondary"}
						onClick={handleAsyncCancel}
					>
						{content.common.asyncModal.cancel}
					</Button>
				)}
				{hasMaxRetries && (
					<span id="async-noCancel">
						{content.common.asyncModal.noRetriesLeft}
					</span>
				)}
			</Modal.Footer>
		</AXNUIDefaultModal>
	);
};